import Maybe from 'true-myth/maybe';
import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import type { JobProfileSkill } from '~/shared/data-contract/skill';

export type PendingSuggestedSkillType = 'add' | 'adjust' | 'remove';

export type PendingSuggestedSkillsRecord = Record<PendingSuggestedSkillType, readonly PendingSuggestedSkill[]>;

export type PendingSuggestedSkill = {
    readonly isAccepted: boolean;
    readonly skill: JobProfileSkill;
    readonly type: PendingSuggestedSkillType;
};

export type SkillActionState = {
    readonly pendingSkillSuggestions: Maybe<PendingSuggestedSkillsRecord>;
};

export type SkillActionActions = {
    readonly discardPendingSkillSuggestions: () => void;
    readonly reset: () => void;
    readonly setPendingSkillSuggestions: (suggestions: Maybe<PendingSuggestedSkillsRecord>) => void;
    readonly toggleAcceptanceOfPendingSuggestedSkill: (pendingSkillSuggestion: PendingSuggestedSkill) => void;
};

export type SkillActionStore = SkillActionActions & SkillActionState;

export type SkillActionStoreDependencies = Record<string, never>;

export const createSkillActionStore = (
    _dependencies: SkillActionStoreDependencies = {},
): StoreApi<SkillActionStore> => {
    return createStore<SkillActionStore>((set, get) => ({
        discardPendingSkillSuggestions: () => set({ pendingSkillSuggestions: Maybe.nothing() }),

        pendingSkillSuggestions: Maybe.nothing(),

        reset: () => set({ pendingSkillSuggestions: Maybe.nothing() }),

        setPendingSkillSuggestions: (suggestions) => set({ pendingSkillSuggestions: suggestions }),

        toggleAcceptanceOfPendingSuggestedSkill: (pendingSkillSuggestion) => {
            const { pendingSkillSuggestions } = get();

            if (pendingSkillSuggestions.isNothing) {
                return;
            }

            const updatedPendingSkillSuggestions: PendingSuggestedSkillsRecord = {
                ...pendingSkillSuggestions.value,
                [pendingSkillSuggestion.type]: pendingSkillSuggestions.value[pendingSkillSuggestion.type].map(
                    (suggestion) => {
                        if (suggestion.skill.id === pendingSkillSuggestion.skill.id) {
                            return {
                                ...suggestion,
                                isAccepted: !suggestion.isAccepted,
                            };
                        }

                        return suggestion;
                    },
                ),
            };

            set({ pendingSkillSuggestions: Maybe.just(updatedPendingSkillSuggestions) });
        },
    }));
};
